import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {GRAY_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

import {VISUALIZATION_TYPE_TO_ICON, VISUALIZATION_TYPE_TO_READABLE} from './AnalyticsVisualizationConstants';
import {VisualizationType} from './AnalyticsVisualizationTypes';

const ORDERED_VISUALIZATION_TYPES = [
  VisualizationType.TABLE,
  VisualizationType.BAR_CHART,
  VisualizationType.LINE_CHART,
  VisualizationType.PIE_CHART,
  VisualizationType.SCATTER_PLOT,
  VisualizationType.HEATMAP,
];

export interface VisualizationTypeSelectorProps {
  visualizationType: VisualizationType | null;
  onChange: (newVisualizationType: VisualizationType) => void;
  disabledVisualizationTypes?: Array<VisualizationType>;
  isDisabled?: boolean;
}

export default function VisualizationTypeSelector({
  visualizationType,
  onChange,
  disabledVisualizationTypes,
  isDisabled = false,
}: VisualizationTypeSelectorProps): JSX.Element {
  function handleSelect(newVisualizationType: VisualizationType): void {
    if (visualizationType !== newVisualizationType) {
      onChange(newVisualizationType);
    }
  }

  const orderedVisualizationTypes =
    disabledVisualizationTypes != null
      ? ORDERED_VISUALIZATION_TYPES.filter(
          (visualizationType) => !disabledVisualizationTypes.includes(visualizationType)
        )
      : ORDERED_VISUALIZATION_TYPES;

  const menu = (
    <Menu dropLeft={true} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={visualizationType}>
      {orderedVisualizationTypes.map((typeOption) => (
        <Option
          key={typeOption}
          icon={VISUALIZATION_TYPE_TO_ICON[typeOption]}
          onSelect={handleSelect}
          value={typeOption}
          dataTestAttrs={{
            component: 'VisualizationTypeSelector',
            element: 'visualizationType',
            key: typeOption,
          }}
        >
          {VISUALIZATION_TYPE_TO_READABLE[typeOption]}
        </Option>
      ))}
    </Menu>
  );
  const displayName =
    visualizationType != null ? VISUALIZATION_TYPE_TO_READABLE[visualizationType] : 'Select a chart type';
  const dataTestAttrs = {
    component: 'VisualizationTypeSelector',
    element: 'button',
  };

  return (
    <SelectDropdown
      disabled={isDisabled}
      menu={menu}
      displayName={
        <>
          {visualizationType != null && (
            <BenchlingIcon
              className={css(styles.displayNameIcon)}
              icon={VISUALIZATION_TYPE_TO_ICON[visualizationType]}
            />
          )}
          <span className="u-mini-spacing-left">{displayName}</span>
        </>
      }
      dataTestAttrs={dataTestAttrs}
    />
  );
}

const styles = StyleSheet.create({
  displayNameIcon: {
    color: GRAY_DARK,
  },
});
